import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
    Box,
    Chip,
    MenuItem,
    Select,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { TrendingDown, TrendingUp, HistoryRounded } from "@mui/icons-material";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { colors } from "../../theme/colors";
import { getMapMetaById } from "../../constants/mapMeta";
import { EmptyDataState } from "../common/EmptyDataState";

const MotionBox = motion(Box);

const DIMENSION_LABELS = {
    sportsmanship: "Sportsmanship",
    integrity: "Integrity",
    communication: "Communication",
    teamPlay: "Team Play",
    reliability: "Reliability",
    reporterQuality: "Reporter Quality",
    profileCompleteness: "Profile Completeness",
};

const DIMENSION_OPTIONS = [
    { value: "all", label: "All Dimensions" },
    { value: "sportsmanship", label: "Sportsmanship" },
    { value: "integrity", label: "Integrity" },
    { value: "communication", label: "Communication" },
    { value: "teamPlay", label: "Team Play" },
    { value: "reliability", label: "Reliability" },
    { value: "reporterQuality", label: "Reporter Quality" },
    { value: "profileCompleteness", label: "Profile Completeness" },
];

const DIMENSION_FILTER_SX = {
    minWidth: "100%",
    maxWidth: "100%",
    width: "100%",
    alignSelf: "stretch",
    background: colors.background.surface,
    borderRadius: { xs: "8px", sm: "10px" },
    minHeight: { xs: "2rem", sm: "2.25rem" },
    padding: { xs: "0.35rem 0.55rem", sm: "0.45rem 0.7rem" },
    transition: "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
    "& .MuiOutlinedInput-notchedOutline": {
        borderColor: colors.border.default,
        borderWidth: "1px",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: alpha(colors.primary.main, 0.55),
    },
    "&.Mui-focused": {
        boxShadow: `0 0 0 3px ${alpha(colors.primary.main, 0.1)}`,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: alpha(colors.primary.main, 0.55),
        borderWidth: "1px",
    },
    "& .MuiSelect-select": {
        color: colors.text.primary,
        fontSize: { xs: "0.75rem", sm: "0.8rem" },
        fontFamily: "inherit",
        fontWeight: 600,
        padding: "0 !important",
        display: "flex",
        alignItems: "center",
        minHeight: "unset !important",
        pr: { xs: "1.35rem", sm: "1.5rem" },
    },
    "& .MuiSelect-icon": {
        color: colors.text.muted,
        right: { xs: "0.3rem", sm: "0.45rem" },
        fontSize: "1.15rem",
    },
};

function DimensionTag({ label }) {
    return (
        <Box
            component="span"
            sx={{
                display: "inline-flex",
                alignItems: "center",
                fontSize: "0.65rem",
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: colors.text.secondary,
                padding: "3px 8px",
                borderRadius: "6px",
                backgroundColor: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                lineHeight: 1.2,
                whiteSpace: "nowrap",
            }}
        >
            {label}
        </Box>
    );
}

DimensionTag.propTypes = {
    label: PropTypes.string.isRequired,
};

function ChangesCard({ entry, index }) {
    const isPositive = entry.change > 0;
    const changeDisplay = isPositive ? `+${entry.change}` : entry.change;
    const changeColor = isPositive ? "#00C853" : "#F44336";
    const dateLabel = format(new Date(entry.timestamp), "MMM dd, yyyy HH:mm");

    return (
        <MotionBox
            whileHover={{ y: -2 }}
            sx={{
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                gap: "0.7rem",
                px: "0.95rem",
                py: "0.95rem",
                borderRadius: "10px",
                overflow: "hidden",
                position: "relative",
                background: `
                    linear-gradient(
                        180deg,
                        rgba(18,20,22,0.88) 0%,
                        rgba(12,14,16,0.94) 100%
                    )
                `,
                border: "1px solid rgba(255,255,255,0.04)",
                backdropFilter: "blur(14px)",
                transition: `
                    transform 260ms cubic-bezier(0.22,1,0.36,1),
                    border-color 260ms cubic-bezier(0.22,1,0.36,1),
                    background 260ms cubic-bezier(0.22,1,0.36,1),
                    box-shadow 260ms cubic-bezier(0.22,1,0.36,1)
                `,
                boxShadow: `
                    inset 0 1px 0 rgba(255,255,255,0.03),
                    0 8px 24px rgba(0,0,0,0.22)
                `,
                "&:hover": {
                    borderColor: "rgba(255,255,255,0.1)",
                    background: `
                        linear-gradient(
                            180deg,
                            rgba(22,24,26,0.96),
                            rgba(14,16,18,0.98)
                        )
                    `,
                    boxShadow: `
                        inset 0 1px 0 rgba(255,255,255,0.05),
                        0 14px 40px rgba(0,0,0,0.34)
                    `,
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "0.75rem",
                }}
            >
                <Typography
                    sx={{
                        fontSize: "0.82rem",
                        fontWeight: 600,
                        color: "rgba(255,255,255,0.55)",
                        lineHeight: 1.2,
                    }}
                >
                    {dateLabel}
                </Typography>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.25rem",
                        color: changeColor,
                        fontWeight: 700,
                    }}
                >
                    {isPositive ? (
                        <TrendingUp sx={{ fontSize: "1rem" }} />
                    ) : (
                        <TrendingDown sx={{ fontSize: "1rem" }} />
                    )}
                    <Typography
                        sx={{
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            fontVariantNumeric: "tabular-nums",
                            lineHeight: 1,
                        }}
                    >
                        {changeDisplay}
                    </Typography>
                </Box>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "0.35rem",
                    minWidth: 0,
                }}
            >
                {entry.source === "match_outcome" && (
                    <Chip
                        label="Match Update"
                        size="small"
                        sx={{
                            fontSize: "0.7rem",
                            height: "22px",
                            backgroundColor: "rgba(16, 185, 129, 0.18)",
                            color: "#10B981",
                        }}
                    />
                )}
                <Typography
                    component="span"
                    sx={{
                        fontSize: "0.82rem",
                        color: colors.text.primary,
                        lineHeight: 1.3,
                    }}
                >
                    {entry.reason}
                </Typography>
                {entry.map && (
                    <Typography
                        component="span"
                        sx={{
                            fontSize: "0.75rem",
                            color: "rgba(255,255,255,0.55)",
                        }}
                    >
                        {getMapMetaById(entry.map)?.display || entry.map}
                    </Typography>
                )}
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
                {entry.dimensionsAffected?.length
                    ? entry.dimensionsAffected.map((dim) => (
                        <DimensionTag
                            key={dim}
                            label={DIMENSION_LABELS[dim] || dim}
                        />
                    ))
                    : (
                        <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.55)" }}>
                            N/A
                        </Typography>
                    )}
            </Box>
        </MotionBox>
    );
}

ChangesCard.propTypes = {
    entry: PropTypes.object.isRequired,
    index: PropTypes.number,
};

export default function ProfileReputationChanges({ history = [] }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [filterDimension, setFilterDimension] = useState("all");
    const [filterOpen, setFilterOpen] = useState(false);
    const safeHistory = Array.isArray(history) ? history : [];

    const selectedLabel = useMemo(
        () => DIMENSION_OPTIONS.find((option) => option.value === filterDimension)?.label
            || "All Dimensions",
        [filterDimension],
    );

    useEffect(() => {
        if (!filterOpen) return undefined;

        const closeOnPageScroll = (event) => {
            const target = event.target;
            if (
                target instanceof Element
                && target.closest(".MuiMenu-paper, .MuiPopover-paper, .MuiModal-root")
            ) {
                return;
            }
            setFilterOpen(false);
        };

        window.addEventListener("scroll", closeOnPageScroll, true);
        return () => {
            window.removeEventListener("scroll", closeOnPageScroll, true);
        };
    }, [filterOpen]);

    const filteredHistory = safeHistory.filter((entry) => {
        if (filterDimension === "all") return true;
        return entry.dimensionsAffected?.includes(filterDimension);
    });

    return (
        <Box
            sx={{
                position: "relative",
                zIndex: 2,
                display: "flex",
                flexDirection: "column",
                width: "100%",
                minWidth: 0,
            }}
        >
            <Typography
                sx={{
                    fontSize: "0.8rem",
                    fontWeight: 800,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: colors.text.primary,
                    lineHeight: 1,
                    mb: "0.35rem",
                }}
            >
                Recent reputation changes
            </Typography>
            <Typography
                sx={{
                    fontSize: "0.725rem",
                    fontWeight: 500,
                    letterSpacing: "0.04em",
                    color: "rgba(255,255,255,0.42)",
                    mb: "1rem",
                }}
            >
                {safeHistory.length} total{" "}
                {safeHistory.length === 1 ? "change" : "changes"}
            </Typography>

            <Select
                value={filterDimension}
                open={filterOpen}
                onOpen={() => setFilterOpen(true)}
                onClose={() => setFilterOpen(false)}
                onChange={(event) => setFilterDimension(event.target.value)}
                displayEmpty
                renderValue={() => selectedLabel}
                sx={DIMENSION_FILTER_SX}
                MenuProps={{
                    disableScrollLock: true,
                    PaperProps: {
                        onWheel: (event) => {
                            event.stopPropagation();
                        },
                        sx: {
                            backgroundColor: colors.background.dark,
                            border: `1px solid ${colors.border.default}`,
                            borderRadius: "14px",
                            mt: "0.35rem",
                            boxShadow: "0 12px 32px rgba(0,0,0,0.45)",
                            backgroundImage: "none",
                            "& .MuiList-root": {
                                scrollbarWidth: "thin",
                                scrollbarColor: "rgba(255,255,255,0.18) transparent",
                                "&::-webkit-scrollbar": {
                                    width: 6,
                                },
                                "&::-webkit-scrollbar-track": {
                                    background: "transparent",
                                },
                                "&::-webkit-scrollbar-thumb": {
                                    backgroundColor: "rgba(255,255,255,0.16)",
                                    borderRadius: 999,
                                },
                                "&::-webkit-scrollbar-thumb:hover": {
                                    backgroundColor: "rgba(255,255,255,0.28)",
                                },
                            },
                        },
                    },
                    MenuListProps: {
                        onWheel: (event) => {
                            event.stopPropagation();
                        },
                        sx: {
                            // ~4 items on mobile; rest scroll inside the menu
                            maxHeight: isMobile ? "10.5rem" : "18.5rem",
                            overflowY: "auto",
                            overscrollBehavior: "contain",
                            py: 0,
                            "& .MuiMenuItem-root": {
                                fontSize: { xs: "0.85rem", sm: "0.95rem" },
                                minHeight: { xs: "2.5rem", sm: "auto" },
                                py: { xs: "0.45rem", sm: "0.6rem" },
                                color: colors.text.primary,
                                "&.Mui-selected": {
                                    backgroundColor: `${alpha(colors.primary.main, 0.24)} !important`,
                                    color: colors.accent.main,
                                },
                                "&.Mui-selected:hover": {
                                    backgroundColor: `${alpha(colors.primary.main, 0.32)} !important`,
                                },
                                "&.Mui-focusVisible": {
                                    backgroundColor: "rgba(255,255,255,0.05)",
                                },
                                "&:hover": {
                                    backgroundColor: "rgba(255,255,255,0.05)",
                                },
                            },
                        },
                    },
                }}
            >
                {DIMENSION_OPTIONS.map((option) => {
                    const isSelected = filterDimension === option.value;
                    return (
                        <MenuItem
                            key={option.value}
                            value={option.value}
                            sx={{
                                display: "flex",
                                width: "100%",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "0.75rem",
                                "&.Mui-selected": {
                                    backgroundColor: `${alpha(colors.primary.main, 0.24)} !important`,
                                    color: colors.accent.main,
                                },
                                "&.Mui-selected:hover": {
                                    backgroundColor: `${alpha(colors.primary.main, 0.32)} !important`,
                                },
                                "&.Mui-focusVisible": {
                                    backgroundColor: "rgba(255,255,255,0.05)",
                                },
                                "&:hover": {
                                    backgroundColor: "rgba(255,255,255,0.05)",
                                },
                            }}
                        >
                            <Box component="span" sx={{ minWidth: 0 }}>
                                {option.label}
                            </Box>
                            {isSelected ? (
                                <Box
                                    component="span"
                                    aria-hidden
                                    sx={{
                                        flexShrink: 0,
                                        fontSize: "0.85rem",
                                        fontWeight: 700,
                                        lineHeight: 1,
                                        color: colors.accent.soft,
                                    }}
                                >
                                    ✓
                                </Box>
                            ) : null}
                        </MenuItem>
                    );
                })}
            </Select>

            {filteredHistory.length === 0 ? (
                <EmptyDataState
                    title={
                        filterDimension === "all"
                            ? "No reputation changes"
                            : "No changes for filter"
                    }
                    icon={HistoryRounded}
                    minHeight="10rem"
                    embedded
                    sx={{ mt: "0.75rem" }}
                >
                    {filterDimension === "all"
                        ? "Reputation changes will appear here as your score updates"
                        : `No changes for ${DIMENSION_LABELS[filterDimension] || filterDimension}`}
                </EmptyDataState>
            ) : (
                <Box
                    sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.65rem",
                        mt: "1rem",
                    }}
                >
                    {filteredHistory.map((entry, index) => (
                        <ChangesCard
                            key={`${entry.timestamp}-${index}`}
                            entry={entry}
                            index={index}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
}

ProfileReputationChanges.propTypes = {
    history: PropTypes.arrayOf(
        PropTypes.shape({
            timestamp: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.instanceOf(Date),
            ]).isRequired,
            change: PropTypes.number.isRequired,
            reason: PropTypes.string.isRequired,
            dimensionsAffected: PropTypes.arrayOf(PropTypes.string),
            source: PropTypes.string,
            map: PropTypes.string,
            matchId: PropTypes.string,
        }),
    ),
};
